import { motion } from "framer-motion";
import { Users, Luggage, Snowflake, Wifi } from "lucide-react";
import { waLink, WhatsAppIcon } from "@/lib/site";
import { T, useLang } from "@/lib/i18n";

const IMAGES = {
  vclass: { img: "/images/vclass_clean.webp", alt: "Black Mercedes V-Class in Salzburg old town" },
  eclass: { img: "/images/eclass_clean.webp", alt: "Black Mercedes E-Class on Salzburg cobblestone street" },
  interior: { img: "/images/interior.webp", alt: "AMG luxury interior with red leather steering wheel" },
};

const ROW_TOP = [
  { img: "/images/vclass_clean.webp", alt: "Mercedes V-Class in Salzburg" },
  { img: "/images/interior.webp", alt: "AMG luxury interior" },
  { img: "/images/vclass_interior.webp", alt: "V-Class luxury leather interior" },
  { img: "/images/eclass_clean.webp", alt: "Mercedes E-Class Salzburg" },
];

const SPEC_ICONS = [Users, Luggage, Snowflake, Wifi];

const PhotoRow = ({ images, reverse, testid }) => {
  const strip = (
    <div className="flex items-center shrink-0">
      {images.map((p, i) => (
        <img
          key={`${p.img}-${i}`}
          src={p.img}
          alt={p.alt}
          loading="lazy" decoding="async"
          className="h-44 md:h-60 w-auto rounded-2xl object-cover mx-2.5 border border-stone-800 shadow-lg shadow-black/30"
        />
      ))}
    </div>
  );
  return (
    <div data-testid={testid} className="photo-marquee overflow-hidden py-2">
      <div className={reverse ? "marquee-track-reverse" : "marquee-track"}>
        {strip}
        {strip}
      </div>
    </div>
  );
};

export default function Fleet() {
  const { lang } = useLang();
  const t = T[lang].fleet;

  return (
    <section id="fleet" data-testid="fleet-section" className="relative py-24 lg:py-36 bg-[#201B16] border-y border-stone-800/80 overflow-hidden">
      <img src="/images/fleet-bg.webp" alt="" aria-hidden="true" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-[#16110C]/70" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#201B16] via-[#201B16]/40 to-[#201B16]" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="flex flex-wrap items-end justify-between gap-6"
        >
          <div className="max-w-xl">
            <p className="font-mono-label text-xs uppercase tracking-[0.28em] text-[#C5A059] mb-4">{t.overline}</p>
            <h2 className="font-serif-display font-semibold tracking-tight text-stone-100 text-3xl sm:text-4xl lg:text-5xl">
              {t.h2a}<em className="not-italic text-[#C5A059]">{t.h2b}</em>{t.h2c}
            </h2>
          </div>
          <p className="max-w-sm text-stone-400 text-[15px] leading-relaxed">{t.side}</p>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.9, ease: "easeOut" }}
        className="relative mt-14"
        data-testid="fleet-slideshow"
      >
        <PhotoRow images={ROW_TOP} testid="fleet-row-left" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
        <div className="mt-14 grid md:grid-cols-2 gap-6 lg:gap-8">
          {t.cars.map((car, i) => {
            const wide = car.testid === "vclass";
            return (
              <motion.article
                key={car.testid}
                data-testid={`fleet-card-${car.testid}`}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.12, ease: "easeOut" }}
                whileHover={{ scale: 1.02 }}
                className={`group rounded-3xl overflow-hidden bg-[#2E2720] border border-stone-800 shadow-sm hover:shadow-xl hover:shadow-black/50 hover:border-[#C5A059]/25 transition-all duration-500 ${wide ? "md:col-span-2" : ""}`}
              >
                <div className={`relative overflow-hidden ${wide ? "aspect-[21/9]" : "aspect-[4/3]"}`}>
                  <img
                    src={IMAGES[car.testid].img}
                    alt={IMAGES[car.testid].alt}
                    loading="lazy" decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 rounded-full bg-black/60 backdrop-blur px-4 py-1.5 text-xs font-semibold tracking-wide text-stone-100 border border-white/20">
                    {car.tag}
                  </span>
                </div>
                <div className="p-7 lg:p-8 flex flex-wrap items-center justify-between gap-5">
                  <div>
                    <h3 className="font-serif-display font-semibold text-2xl text-stone-100">{car.title}</h3>
                    <p data-testid={`fleet-ride-${car.testid}`} className="mt-1 text-sm italic text-[#C5A059]/90">{t.ride}</p>
                    <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
                      {car.specs.map((label, j) => {
                        const Icon = SPEC_ICONS[j];
                        return (
                          <span key={label} className="flex items-center gap-1.5 text-xs text-stone-400 font-medium">
                            <Icon className="w-3.5 h-3.5 text-[#C5A059]" /> {label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  <a
                    data-testid={`fleet-book-${car.testid}`}
                    href={waLink(car.wa)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-full bg-[#C5A059] text-[#16110C] px-5 py-3 text-sm font-semibold hover:bg-[#25D366] hover:text-white transition-colors duration-300"
                  >
                    <WhatsAppIcon className="w-4 h-4" />
                    {t.book}
                  </a>
                </div>
              </motion.article>
            );
          })}
          <motion.article
            data-testid="fleet-card-multi"
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.12, ease: "easeOut" }}
            whileHover={{ scale: 1.02 }}
            className="group rounded-3xl overflow-hidden bg-[#2E2720] border border-stone-800 shadow-sm hover:shadow-xl hover:shadow-black/50 hover:border-[#C5A059]/25 transition-all duration-500"
          >
            <div className="relative overflow-hidden aspect-[4/3]">
              <img
                src="/images/fleet_night.webp"
                alt="Two black Mercedes vehicles at night below Salzburg fortress"
                loading="lazy" decoding="async"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            </div>
            <div className="p-7 lg:p-8">
              <h3 className="font-serif-display font-semibold text-2xl text-stone-100">{t.multi.title}</h3>
              <p className="mt-2 text-sm text-stone-400 leading-relaxed">{t.multi.text}</p>
              <a
                data-testid="fleet-book-multi"
                href={waLink(t.multi.wa)}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#C5A059] text-[#16110C] px-5 py-3 text-sm font-semibold hover:bg-[#25D366] hover:text-white transition-colors duration-300"
              >
                <WhatsAppIcon className="w-4 h-4" />
                {t.multi.cta}
              </a>
            </div>
          </motion.article>
        </div>
      </div>
    </section>
  );
}
